/* eslint-disable no-bitwise */
import { BitWriter } from '../flac/bitWriter';
import type { AudioSampleEntry, ChunkOffsetBox, DataEntryUrlBox, DataReferenceBox, EditListBox, FLACSampleEntry, FLACSpecificBox, FullBox, HandlerBox, MediaHeaderBox, MovieFragmentHeaderBox, MovieFragmentRandomAccessOffsetBox, MovieHeaderBox, Mp4Box, SampleDescriptionBox, SampleEntry, SampleSizeBox, SampleToChunkBox, SoundMediaHeaderBox, TimeToSampleBox, TrackExtendsBox, TrackFragmentBaseMediaDecodeTimeBox, TrackFragmentHeaderBox, TrackFragmentRandomAccessBox, TrackHeaderBox, TrackRunBox } from './parser';
import { invariant } from '../util/invariant';
import { concatByteArrays } from '../util/byteArray';


export function encodeBox(box: Mp4Box): Uint8Array {
  const buffers: Uint8Array[] = [];

  const bitWriter = new BitWriter();
  writeBoxProperties(bitWriter, box);
  buffers.push(bitWriter.getBuffer());
  for (const child of box.children) {
    buffers.push(encodeBox(child));
  }

  let size = 8;
  for (const b of buffers) {
    size += b.byteLength;
  }

  const bitWriter2 = new BitWriter();
  bitWriter2.writeBits(size, 32);
  bitWriter2.writeString(box.type);

  return concatByteArrays([bitWriter2.getBuffer(), ...buffers]);
}


function writeBoxProperties(bitWriter: BitWriter, box: Mp4Box) {
  switch (box.type) {
    case 'dref': writeDataReferenceBoxProperties(bitWriter, box as DataReferenceBox); break;
    case 'mvhd': writeMovieHeaderBoxProperties(bitWriter, box as MovieHeaderBox); break;
    case 'tkhd': writeTrackHeaderBoxProperties(bitWriter, box as TrackHeaderBox); break;
    case 'elst': writeEditListBoxProperties(bitWriter, box as EditListBox); break;
    case 'mdhd': writeMediaHeaderBoxProperties(bitWriter, box as MediaHeaderBox); break;
    case 'hdlr': writeHandlerBoxProperties(bitWriter, box as HandlerBox); break;
    case 'smhd': writeSoundMediaHeaderBoxProperties(bitWriter, box as SoundMediaHeaderBox); break;
    case 'url ': writeDataEntryUrlBoxProperties(bitWriter, box as DataEntryUrlBox); break;
    case 'fLaC': writeFLACSampleEntryProperties(bitWriter, box as FLACSampleEntry); break;
    case 'dfLa': writeFLACSpecificBoxProperties(bitWriter, box as FLACSpecificBox); break;
    case 'stsd': writeSampleDescriptionBoxProperties(bitWriter, box as SampleDescriptionBox); break;
    case 'stts': writeTimeToSampleBoxProperties(bitWriter, box as TimeToSampleBox); break;
    case 'stsc': writeSampleToChunkBoxProperties(bitWriter, box as SampleToChunkBox); break;
    case 'stsz': writeSampleSizeBoxProperties(bitWriter, box as SampleSizeBox); break;
    case 'stco': writeChunkOffsetBoxProperties(bitWriter, box as ChunkOffsetBox); break;
    case 'trex': writeTrackExtendsBoxProperties(bitWriter, box as TrackExtendsBox); break;
    case 'mfhd': writeMovieFragmentHeaderBoxProperties(bitWriter, box as MovieFragmentHeaderBox); break;
    case 'free': writeFreeBox(bitWriter, box); break;
    case 'tfhd': writeTrackFragmentHeaderBoxProperties(bitWriter, box as TrackFragmentHeaderBox); break;
    case 'tfdt': writeTrackFragmentBaseMediaDecodeTimeBoxProperties(bitWriter, box as TrackFragmentBaseMediaDecodeTimeBox); break;
    case 'trun': writeTrackRunBoxProperties(bitWriter, box as TrackRunBox); break;
    case 'tfra': writeTrackFragmentRandomAccessBoxProperties(bitWriter, box as TrackFragmentRandomAccessBox); break;
    case 'mfro': writeMovieFragmentRandomAccessOffsetBoxProperties(bitWriter, box as MovieFragmentRandomAccessOffsetBox); break;
  }
}


function writeFreeBox(bitWriter: BitWriter, box: Mp4Box) {
  for (let i = 8; i < box.size; i++) {
    bitWriter.writeBits(0, 8);
  }
}


function writeFullBoxProperties(bitWriter: BitWriter, box: FullBox) {
  bitWriter.writeBits(box.version, 8);
  bitWriter.writeBits(box.flags, 24);
}


function writeDataReferenceBoxProperties(bitWriter: BitWriter, box: DataReferenceBox) {
  writeFullBoxProperties(bitWriter, box);

  bitWriter.writeBits(box.children.length, 32);
}


function writeSampleDescriptionBoxProperties(bitWriter: BitWriter, box: SampleDescriptionBox) {
  writeFullBoxProperties(bitWriter, box);

  bitWriter.writeBits(box.children.length, 32);
}

function writeMovieHeaderBoxProperties(bitWriter: BitWriter, box: MovieHeaderBox) {
  writeFullBoxProperties(bitWriter, box);
  invariant(box.version === 0, `Unexpected version ${box.version} in writeMovieHeaderBoxProperties()`);

  bitWriter.writeBits(box.creation_time, 32);
  bitWriter.writeBits(box.modification_time, 32);
  bitWriter.writeBits(box.timescale, 32);
  bitWriter.writeBits(box.duration, 32);

  bitWriter.writeBits(box.rate, 32);
  bitWriter.writeBits(box.volume, 16);

  // reserved
  bitWriter.writeBits(0, 16);
  bitWriter.writeBits(0, 32);
  bitWriter.writeBits(0, 32);

  invariant(box.matrix.length === 9, `Unexpected matrix length ${box.matrix.length} in writeMovieHeaderBoxProperties()`);
  for (let i = 0; i < box.matrix.length; i++) {
    bitWriter.writeBits(box.matrix[i], 32);
  }

  // reserved
  for (let i = 0; i < 6; i++) {
    bitWriter.writeBits(0, 32);
  }

  bitWriter.writeBits(box.next_track_id, 32);
}


function writeTrackHeaderBoxProperties(bitWriter: BitWriter, box: TrackHeaderBox) {
  writeFullBoxProperties(bitWriter, box);
  invariant(box.version === 0, `Unexpected version ${box.version} in writeTrackHeaderBoxProperties()`);

  bitWriter.writeBits(box.creation_time, 32);
  bitWriter.writeBits(box.modification_time, 32);
  bitWriter.writeBits(box.track_id, 32);

  // reserved
  bitWriter.writeBits(0, 32);
  bitWriter.writeBits(box.duration, 32);

  // reserved
  bitWriter.writeBits(0, 32);
  bitWriter.writeBits(0, 32);

  bitWriter.writeBits(box.layer, 16);
  bitWriter.writeBits(box.alternate_group, 16);
  bitWriter.writeBits(box.volume, 16);

  // reserved
  bitWriter.writeBits(0, 16);

  invariant(box.matrix.length === 9, `Unexpected matrix length ${box.matrix.length} in writeTrackHeaderBoxProperties()`);
  for (let i = 0; i < box.matrix.length; i++) {
    bitWriter.writeBits(box.matrix[i], 32);
  }

  bitWriter.writeBits(box.width, 32);
  bitWriter.writeBits(box.height, 32);
}


function writeEditListBoxProperties(bitWriter: BitWriter, box: EditListBox) {
  invariant(box.version === 0, `Unexpected version ${box.version} in writeEditListBoxProperties()`);
  writeFullBoxProperties(bitWriter, box);

  bitWriter.writeBits(box.entries.length, 32);

  for (let i = 0; i < box.entries.length; i++) {
    bitWriter.writeBits(box.entries[i].segment_duration, 32);
    bitWriter.writeBits(box.entries[i].media_time, 32);
    bitWriter.writeBits(box.entries[i].media_rate_integer, 16);
    bitWriter.writeBits(box.entries[i].media_rate_fraction, 16);
  }
}


function writeMediaHeaderBoxProperties(bitWriter: BitWriter, box: MediaHeaderBox) {
  writeFullBoxProperties(bitWriter, box);
  invariant(box.version === 0, `Unexpected version ${box.version} in writeMediaHeaderBoxProperties()`);

  bitWriter.writeBits(box.creation_time, 32);
  bitWriter.writeBits(box.modification_time, 32);
  bitWriter.writeBits(box.timescale, 32);
  bitWriter.writeBits(box.duration, 32);

  bitWriter.writeBits(box.language, 16);

  // reserved
  bitWriter.writeBits(0, 16);
}


function writeHandlerBoxProperties(bitWriter: BitWriter, box: HandlerBox) {
  writeFullBoxProperties(bitWriter, box);

  // reserved
  bitWriter.writeBits(0, 32);

  bitWriter.writeString(box.handler_type);

  // reserved
  bitWriter.writeBits(0, 32);
  bitWriter.writeBits(0, 32);
  bitWriter.writeBits(0, 32);

  // name is a null terminated utf8 string, so we need to add
  // one 0 after the string bytes
  bitWriter.writeString(box.name);
  bitWriter.writeBits(0, 8);
}


function writeSoundMediaHeaderBoxProperties(bitWriter: BitWriter, box: SoundMediaHeaderBox) {
  writeFullBoxProperties(bitWriter, box);

  bitWriter.writeBits(box.balance, 16);

  // reserved
  bitWriter.writeBits(0, 16);
}


function writeDataEntryUrlBoxProperties(bitWriter: BitWriter, box: DataEntryUrlBox) {
  invariant(box.flags === 1, `Unexpected flags ${box.flags} in writeDataEntryUrlBoxProperties()`);
  writeFullBoxProperties(bitWriter, box);
}


function writeSampleEntryProperties(bitWriter: BitWriter, box: SampleEntry) {
  // reserved
  for (let i = 0; i < 6; i++) {
    bitWriter.writeBits(0, 8);
  }

  bitWriter.writeBits(box.data_reference_index, 16);
}


function writeAudioSampleEntryProperties(bitWriter: BitWriter, box: AudioSampleEntry) {
  writeSampleEntryProperties(bitWriter, box);

  // reserved
  bitWriter.writeBits(0, 32);
  bitWriter.writeBits(0, 32);

  bitWriter.writeBits(box.channelcount, 16);
  bitWriter.writeBits(box.samplesize, 16);

  // reserved
  bitWriter.writeBits(0, 16);
  bitWriter.writeBits(0, 16);

  bitWriter.writeBits(box.samplerate, 16);
  bitWriter.writeBits(0, 16);
}


function writeFLACSampleEntryProperties(bitWriter: BitWriter, box: FLACSampleEntry) {
  writeAudioSampleEntryProperties(bitWriter, box);
}


function writeFLACSpecificBoxProperties(bitWriter: BitWriter, box: FLACSpecificBox) {
  writeFullBoxProperties(bitWriter, box);

  invariant(box.flacMetadataBlocks.length === 1, `Unexpected flacMetadataBlocks.length ${box.flacMetadataBlocks.length} in writeFLACSpecificBoxProperties()`);

  const block = box.flacMetadataBlocks[0];
  invariant(block.LastMetadataBlockFlag, `Unexpected LastMetadataBlockFlag ${block.LastMetadataBlockFlag} in writeFLACSpecificBoxProperties()`);
  invariant(block.BlockType === 0, `Unexpected BlockType ${block.BlockType} in writeFLACSpecificBoxProperties()`);

  bitWriter.writeBits(1, 1);
  bitWriter.writeBits(0, 7);
  bitWriter.writeBits(block.BlockData.length, 24);

  for (let i = 0; i < block.BlockData.length; i++) {
    bitWriter.writeBits(block.BlockData[i], 8);
  }
}


function writeTimeToSampleBoxProperties(bitWriter: BitWriter, box: TimeToSampleBox) {
  writeFullBoxProperties(bitWriter, box);

  bitWriter.writeBits(box.sample_count.length, 32);

  for (let i = 0; i < box.sample_count.length; i++) {
    bitWriter.writeBits(box.sample_count[i], 32);
    bitWriter.writeBits(box.sample_delta[i], 32);
  }

}


function writeSampleToChunkBoxProperties(bitWriter: BitWriter, box: SampleToChunkBox) {
  writeFullBoxProperties(bitWriter, box);

  bitWriter.writeBits(box.first_chunk.length, 32);

  for (let i = 0; i < box.first_chunk.length; i++) {
    bitWriter.writeBits(box.first_chunk[i], 32);
    bitWriter.writeBits(box.samples_per_chunk[i], 32);
    bitWriter.writeBits(box.sample_description_index[i], 32);
  }
}


function writeSampleSizeBoxProperties(bitWriter: BitWriter, box: SampleSizeBox) {
  writeFullBoxProperties(bitWriter, box);

  invariant(box.sample_size === 0, `Unexpected sample_size ${box.sample_size} in writeSampleSizeBoxProperties()`);
  bitWriter.writeBits(box.sample_size, 32);

  invariant(box.sample_count === box.sample_sizes.length, `Mismatch betwen sample_count ${box.sample_count} and sample_sizes.length ${box.sample_sizes.length} in writeSampleSizeBoxProperties()`);
  bitWriter.writeBits(box.sample_count, 32);

  for (let i = 0; i < box.sample_count; i++) {
    bitWriter.writeBits(box.sample_sizes[i], 32);
  }

}


function writeChunkOffsetBoxProperties(bitWriter: BitWriter, box: ChunkOffsetBox) {
  writeFullBoxProperties(bitWriter, box);

  bitWriter.writeBits(box.chunk_offsets.length, 32);

  for (let i = 0; i < box.chunk_offsets.length; i++) {
    bitWriter.writeBits(box.chunk_offsets[i], 32);
  }
}


function writeTrackExtendsBoxProperties(bitWriter: BitWriter, box: TrackExtendsBox) {
  writeFullBoxProperties(bitWriter, box);

  bitWriter.writeBits(box.track_ID, 32);
  bitWriter.writeBits(box.default_sample_description_index, 32);
  bitWriter.writeBits(box.default_sample_duration, 32);
  bitWriter.writeBits(box.default_sample_size, 32);
  bitWriter.writeBits(box.default_sample_flags, 32);
}


function writeMovieFragmentHeaderBoxProperties(bitWriter: BitWriter, box: MovieFragmentHeaderBox) {
  writeFullBoxProperties(bitWriter, box);

  bitWriter.writeBits(box.sequence_number, 32);
}


function writeTrackFragmentHeaderBoxProperties(bitWriter: BitWriter, box: TrackFragmentHeaderBox) {
  writeFullBoxProperties(bitWriter, box);

  bitWriter.writeBits(box.track_ID, 32);

  if ((box.flags & 0x000001) !== 0) {
    invariant(box.base_data_offset !== undefined, 'box.base_data_offset should be defined');
    bitWriter.writeBits(0, 32);
    bitWriter.writeBits(box.base_data_offset, 32);
  }

  if ((box.flags & 0x000002) !== 0) {
    invariant(box.sample_description_index !== undefined, 'box.sample_description_index should be defined');
    bitWriter.writeBits(box.sample_description_index, 32);
  }

  if ((box.flags & 0x000008) !== 0) {
    invariant(box.default_sample_duration !== undefined, 'box.default_sample_duration should be defined');
    bitWriter.writeBits(box.default_sample_duration, 32);
  }

  if ((box.flags & 0x000010) !== 0) {
    invariant(box.default_sample_size !== undefined, 'box.default_sample_size should be defined');
    bitWriter.writeBits(box.default_sample_size, 32);
  }

  if ((box.flags & 0x000020) !== 0) {
    invariant(box.default_sample_flags !== undefined, 'box.default_sample_flags should be defined');
    bitWriter.writeBits(box.default_sample_flags, 32);
  }
}


function writeTrackFragmentBaseMediaDecodeTimeBoxProperties(bitWriter: BitWriter, box: TrackFragmentBaseMediaDecodeTimeBox) {
  writeFullBoxProperties(bitWriter, box);

  invariant(box.version === 1, `Unexpected version ${box.version} in writeEditListBoxProperties()`);
  bitWriter.writeBits(0, 32);
  bitWriter.writeBits(box.baseMediaDecodeTime, 32);
}


function writeTrackRunBoxProperties(bitWriter: BitWriter, box: TrackRunBox) {
  writeFullBoxProperties(bitWriter, box);

  bitWriter.writeBits(box.sample_count, 32);

  if ((box.flags & 0x000001) !== 0) {
    invariant(box.data_offset !== undefined, 'box.data_offset should be defined');
    bitWriter.writeBits(box.data_offset, 32);
  }

  if ((box.flags & 0x000004) !== 0) {
    invariant(box.first_sample_flags !== undefined, 'box.first_sample_flags should be defined');
    bitWriter.writeBits(box.first_sample_flags, 32);
  }

  for (let i = 0; i < box.sample_count; i++) {
    const sample = box.samples[i];

    if ((box.flags & 0x000100) !== 0) {
      invariant(sample.sample_duration !== undefined, 'sample.sample_duration should be defined');
      bitWriter.writeBits(sample.sample_duration, 32);
    }

    if ((box.flags & 0x000200) !== 0) {
      invariant(sample.sample_size !== undefined, 'sample.sample_size should be defined');
      bitWriter.writeBits(sample.sample_size, 32);
    }

    if ((box.flags & 0x000400) !== 0) {
      invariant(sample.sample_flags !== undefined, 'sample.sample_flags should be defined');
      bitWriter.writeBits(sample.sample_flags, 32);
    }

    if ((box.flags & 0x000800) !== 0) {
      invariant(sample.sample_composition_time_offset !== undefined, 'sample.sample_composition_time_offset should be defined');
      bitWriter.writeBits(sample.sample_composition_time_offset, 32);
    }
  }
}


function writeTrackFragmentRandomAccessBoxProperties(bitWriter: BitWriter, box: TrackFragmentRandomAccessBox) {
  writeFullBoxProperties(bitWriter, box);

  bitWriter.writeBits(box.track_ID, 32);
  bitWriter.writeBits(0, 26);
  bitWriter.writeBits(box.length_size_of_traf_num, 2);
  bitWriter.writeBits(box.length_size_of_trun_num, 2);
  bitWriter.writeBits(box.length_size_of_sample_num, 2);

  bitWriter.writeBits(box.entries.length, 32);
  for (let i = 0; i < box.entries.length; i++) {
    const entry = box.entries[i];
    if (box.version === 1) {
      bitWriter.writeBits(0, 32);
      bitWriter.writeBits(entry.time, 32);
      bitWriter.writeBits(0, 32);
      bitWriter.writeBits(entry.moof_offset, 32);
    } else {
      bitWriter.writeBits(entry.time, 32);
      bitWriter.writeBits(entry.moof_offset, 32);
    }
    bitWriter.writeBits(entry.traf_number, 8 * (1 + box.length_size_of_traf_num));
    bitWriter.writeBits(entry.trun_number, 8 * (1 + box.length_size_of_trun_num));
    bitWriter.writeBits(entry.sample_number, 8 * (1 + box.length_size_of_sample_num));
  }
}


function writeMovieFragmentRandomAccessOffsetBoxProperties(bitWriter: BitWriter, box: MovieFragmentRandomAccessOffsetBox) {
  writeFullBoxProperties(bitWriter, box);

  bitWriter.writeBits(box.size, 32);
}
